import type {ReactElement} from 'react';
import React from 'react';
import type {AnimationSpeed, AnimationType} from '@PosterWhiteboard/classes/item-animation.class';
import {SLIDESHOW_INTRO_ANIMATIONS} from '@PosterWhiteboard/classes/item-animation.class';
import {BaseTransitionSpeedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-transition-speed-option';
import {BlockAnimationColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/block-animation-color-option';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {IntroAnimationListItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/intro-animation-list-item';
import {getSvgFromType, getTextFromType} from '@Components/poster-editor/components/poster-editing-side-panel/components/intro-animation-list-item/intro-animation-svgs';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './slideshow-transition-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetNumberOfSlidesForActiveSlideshowItem} from '@Hooks/poster-editor/useGetNumberOfSlidesForActiveSlideshowItem';
import {useGetKeyFromTransitionOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromTransitionOfActiveItem';

interface SlideshowTransitionOptionProps {
  onClickColorFill(): void;
}

export function SlideshowTransitionOption({...props}: SlideshowTransitionOptionProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const numOfSlides = useGetNumberOfSlidesForActiveSlideshowItem();
  const transitionType = useGetKeyFromTransitionOfActiveItem('type');
  const transitionSpeed = useGetKeyFromTransitionOfActiveItem('speed');

  if (!activeItem) {
    throw new Error('Active item is undefined in use slideshow transition option');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected slideshow item instead got ${activeItem.gitype}`);
  }

  if (transitionSpeed === undefined || transitionType === undefined) {
    throw new Error(`transition properties for slideshow item are undefined`);
  }

  if (numOfSlides === 1) {
    return null;
  }

  const onTransitionModified = (id: AnimationType): void => {
    void activeItem.updateFromObject(
      {
        transition: {
          type: id,
        },
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };

  const onToggleSpeed = (id: AnimationSpeed): void => {
    void activeItem.updateFromObject(
      {
        transition: {
          speed: id,
        },
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };

  const getCustomPopup = (): ReactElement<any> => {
    return (
      <ul className={styles.popup}>
        {SLIDESHOW_INTRO_ANIMATIONS.map((animation) => {
          return (
            <IntroAnimationListItem
              key={animation}
              text={getTextFromType(animation)}
              selectedAnimation={transitionType}
              type={animation}
              svg={getSvgFromType(animation)}
              onClick={onTransitionModified}
            />
          );
        })}
      </ul>
    );
  };

  return (
    <PosterEditingBaseDropdownOption
      id="slideshow-transition-option"
      hasDefaultSpacing
      onClick={onTransitionModified}
      label={window.i18next.t('pmwjs_transition')}
      customPopup={getCustomPopup()}
      dropdownSelectorText={getTextFromType(transitionType)}
      selectorSvg={getSvgFromType(transitionType)}
      position={DROPDOWN_POSITION.FIXED}
    >
      {activeItem.hasTransition() ? (
        <>
          <BaseTransitionSpeedOption key="slideshow-transition-speed-suboption" selectedId={transitionSpeed} onToggleSpeed={onToggleSpeed} />
          <BlockAnimationColorOption
            onClickColorFill={(): void => {
              props.onClickColorFill();
            }}
          />
        </>
      ) : null}
    </PosterEditingBaseDropdownOption>
  );
}
